// Sales, Plán (mockup v8, obrazovka 1): komu dnes volat, po skupinách. Od 1. 10. 2026 je záložka
// Plán a nahoře přepínač Dnes, Týden, Měsíc (mockup „Plán volání“), týden a měsíc kreslí PlanVolani.
// Na počítači je vpravo karta vybraného kontaktu, na telefonu se otevře celá stránka karty.
import React, { useEffect, useMemo } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Clock, PhoneCall, RotateCcw, UserRound } from "lucide-react";
import { Dlazdice, EmptyState, Segment } from "../../components/ui.jsx";
import PlanVolani from "../../components/sales/PlanVolani.jsx";
import SalesNastenka from "../../components/sales/SalesNastenka.jsx";
import { getCurrentUser } from "../../lib/auth.js";
import KartaKontaktu from "../../components/sales/KartaKontaktu.jsx";
import { Pil, PruhSkupiny, RadekKontaktu, KartaObal, useSiroka } from "../../components/sales/salesUi.jsx";
import { kdyText, casBezNuly, denKratce, dnesIso, dm } from "../../components/sales/salesApi.js";

function vytvoreno(k) {
  const t = kdyText(k.prisla || k.vytvoreno);
  return t || "";
}

export default function SalesDnes() {
  const { dnes, poZmene } = useOutletContext();
  const siroka = useSiroka();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const vybrany = params.get("k") ? Number(params.get("k")) : null;
  const rezim = ["tyden", "mesic"].includes(params.get("p")) ? params.get("p") : "dnes";
  const nastav = (zmeny) => { const n = new URLSearchParams(params); for (const [a, b] of Object.entries(zmeny)) { if (b) n.set(a, b); else n.delete(a); } setParams(n, { replace: true }); };

  const skupiny = useMemo(() => {
    if (!dnes) return [];
    const s = dnes.skupiny;
    const d = dnesIso();
    return [
      // 20. 10. 2026 (Láďa, mockup 1/3): zaplatila zkušební, ale termín nemá (pozdní platba).
      { klic: "zbt", title: "Zaplaceno, vybrat termín", ton: "ok", polozky: (s.zaplacenoBezTerminu || []).map((k) => ({
        k, pilulky: <>
          <Pil ton="ok" casti={["Zaplaceno, vybrat termín"]} />
          <Pil ton="neutralni" casti={[k.zaplaceno_bez_terminu.zaplaceno ? `zaplaceno ${dm(k.zaplaceno_bez_terminu.zaplaceno)}` : "zaplaceno", k.zaplaceno_bez_terminu.castka ? `${k.zaplaceno_bez_terminu.castka} Kč` : null].filter(Boolean)} />
        </>,
        pozn: "Termín si sama nevybrala. Zavolej a vyber s ní nový, novou fakturu nevystavuj.",
      })) },
      // 19. 10. 2026: po zkušebním u trenéra nezačala, trenér zvolil Zkusit jiného trenéra.
      { klic: "pzk", title: "Nezačala, nabídni jiného trenéra", ton: "pozor", polozky: (s.poZkusebnim || []).map((k) => ({
        k, pilulky: <>
          <Pil ton="pozor" ikona={UserRound} casti={[k.po_zkusebnim.trener, k.po_zkusebnim.zkusebni ? `zkušební ${denKratce(k.po_zkusebnim.zkusebni)}` : null]} />
          {k.po_zkusebnim.duvod_text && <Pil ton="pozor" casti={[k.po_zkusebnim.duvod_text]} />}
          {k.stav === "pokus" && <Pil ton={k.pokus >= 2 ? "problem" : "neutralni"} casti={[`${k.pokus + 1}. pokus`]} />}
        </>,
        pozn: k.po_zkusebnim.poznamka ? `Trenér: „${k.po_zkusebnim.poznamka}“` : "Nabídni jiného trenéra nebo jiný čas tréninku.",
      })) },
      // 19. 10. 2026: po zkušebním nezačala, trenér zvolil Ozvat se za 3 měsíce, teď se vrátila k volání.
      { klic: "vpz", title: "Vrací se po 3 měsících", ton: "zlata", polozky: (s.vraciPoZkusebnim || []).map((k) => ({
        k, pilulky: <>
          <Pil ton="zlata" ikona={UserRound} casti={[k.po_zkusebnim.trener, k.po_zkusebnim.zkusebni ? `zkušební ${denKratce(k.po_zkusebnim.zkusebni)}` : null]} />
          {k.po_zkusebnim.duvod_text && <Pil ton="pozor" casti={[k.po_zkusebnim.duvod_text]} />}
          {k.stav === "pokus" && <Pil ton={k.pokus >= 2 ? "problem" : "neutralni"} casti={[`${k.pokus + 1}. pokus`]} />}
        </>,
        pozn: k.po_zkusebnim.poznamka ? `Trenér: „${k.po_zkusebnim.poznamka}“` : "Zjisti, jestli to teď půjde.",
      })) },
      // 19. 10. 2026: v 1. kole ani jednou nezvedla, po 2 měsících 2. kolo (2 pokusy).
      { klic: "dk2", title: "2. kolo, minule nezvedla", ton: "info", polozky: (s.druheKolo || []).map((k) => ({
        k, pilulky: <>
          <Pil ton="info" ikona={PhoneCall} casti={[`1. kolo ${dm(k.druhe_kolo.kolo1)}`, `${k.druhe_kolo.pokusu}× nezvedla`]} />
          <Pil ton={k.stav === "pokus" ? "problem" : "neutralni"} casti={[`${k.stav === "pokus" ? k.pokus + 1 : 1}. pokus ze 2`, k.stav === "pokus" ? "poslední" : null]} />
        </>,
        pozn: k.poznamka,
      })) },
      // 22. 10. 2026 (Láďa): nedorazila na zkušební, ale omluvila se. Sales nastaví, kdy jí zavolat.
      { klic: "nzo", title: "Nedorazila na zkušební, omluvila se", ton: "pozor", polozky: (s.nedorazilaOmluvena || []).map((k) => ({
        k, pilulky: <>
          <Pil ton="pozor" ikona={UserRound} casti={[k.nedorazila.trener, k.nedorazila.zkusebni ? `zkušební ${denKratce(k.nedorazila.zkusebni)}` : null]} />
          <Pil ton="pozor" casti={["nedorazila", "s omluvou"]} />
          {k.dalsi ? <Pil ton="info" ikona={Clock} casti={[k.dalsi < d ? `od ${denKratce(k.dalsi)}` : (k.dalsi_cas ? casBezNuly(k.dalsi_cas) : "během dne"), "zavolat"]} /> : <Pil ton="problem" casti={["nastav, kdy zavolat"]} />}
        </>,
        pozn: `Systém: ${k.nedorazila.poznamka ? `„${k.nedorazila.poznamka}“ ` : ""}Zapsal(a) ${k.nedorazila.kdo}. Domluv nový termín zkušebního.`,
      })) },
      { klic: "dom", title: "Domluveno na dnes", ton: "znacka", polozky: s.domluveno.map((k) => ({
        k, pilulky: <Pil ton="info" ikona={Clock} casti={[k.dalsi < d ? `od ${denKratce(k.dalsi)}` : (k.dalsi_cas ? casBezNuly(k.dalsi_cas) : "během dne"), "odloženo"]} />, pozn: k.poznamka,
      })) },
      { klic: "dop", title: "Doplnit datum", ton: "pozor", polozky: s.doplnit.map((k) => ({ k, pilulky: <Pil ton="pozor" casti={["převzato z Notionu", k.zdroj].filter(Boolean)} />, pozn: k.poznamka })) },
      // 1. 10. 2026: noví přijdou bez data, sales jim naplánuje první hovor.
      { klic: "nbd", title: "Noví, naplánuj hovor", ton: "pozor", polozky: (s.noviBezData || []).map((k) => ({
        k, pilulky: k.byvala ? <Pil ton="zlata" casti={["bývalá klientka", "bez data"]} /> : <Pil ton="pozor" casti={[k.zdroj || "nový", vytvoreno(k), "bez data"]} />, pozn: k.poznamka,
      })) },
      { klic: "nov", title: "Noví, první hovor", ton: "znacka", polozky: s.novi.map((k) => ({
        k, pilulky: k.byvala ? <Pil ton="zlata" casti={["bývalá klientka", "přidáno k volání"]} /> : <Pil ton="znacka" casti={[k.zdroj || "nový", vytvoreno(k)]} />, pozn: k.poznamka,
      })) },
      { klic: "pok", title: "Další pokus", ton: "pozor", polozky: s.pokusy.map((k) => ({
        k, pilulky: <Pil ton={k.pokus >= 3 ? "problem" : "pozor"} casti={[`${k.pokus + 1}. pokus ze 4`, k.pokus >= 3 ? "poslední" : (k.dalsi < d ? `od ${denKratce(k.dalsi)}` : "")]} />, pozn: k.poznamka,
      })) },
    ].filter((g) => g.polozky.length)
      // 8. 10. 2026 (mockup „Ozvala se znovu“): kontakt, který poptal znovu, má štítek.
      .map((g) => ({ ...g, polozky: g.polozky.map((p) => (p.k.znovu ? { ...p, pilulky: <>{p.pilulky}<Pil ton="pozor" ikona={RotateCcw} casti={["znovu", dm(p.k.znovu.datum)]} /></> } : p)) }));
  }, [dnes]);

  // Na počítači se po načtení vybere první kontakt, ať karta vpravo není prázdná.
  // Kontakty z karty Po splatnosti jsou první, ať se klepnutí na ně nepřepíše výběrem jiného.
  const vsichni = useMemo(() => [...(dnes?.skupiny?.poSplatnosti || []).map((k) => k.id), ...skupiny.flatMap((g) => g.polozky.map((p) => p.k.id))], [dnes, skupiny]);
  // Když vybraný kontakt z fronty zmizí (hovor zapsaný), přejde se na další.
  useEffect(() => {
    if (!siroka || !dnes || rezim !== "dnes") return;
    if ((!vybrany || !vsichni.includes(vybrany)) && vsichni.length) nastav({ k: String(vsichni[0]) });
  }, [siroka, dnes, vybrany, vsichni, rezim]); // eslint-disable-line react-hooks/exhaustive-deps

  const otevri = (id) => {
    if (siroka) nastav({ k: String(id) });
    else navigate(`/sales/kontakt/${id}`);
  };

  // 15. 10. 2026: motivační nástěnka jen pro sales (nad plánem volání).
  const jeSales = String(getCurrentUser()?.role || "").toLowerCase() === "sales";
  const dlazdice = (
    <div className="grid grid-cols-3 gap-2 sm:gap-3">
      <Dlazdice popisek="Volat dnes" hodnota={dnes ? dnes.dlazdice.volatDnes : "…"} ton="znacka" />
      <Dlazdice popisek="Zkušební tento týden" hodnota={dnes ? dnes.dlazdice.zkusebniTyden : "…"} ton="ok" />
      <Dlazdice popisek="Po splatnosti" hodnota={dnes ? dnes.dlazdice.poSplatnosti : "…"} ton="problem" hodnotaTon={dnes?.dlazdice.poSplatnosti ? "problem" : undefined} />
    </div>
  );

  // 18. 10. 2026 (mockup „Po splatnosti na první pohled“): zkušební po splatnosti jsou vidět
  // hned pod dlaždicemi ve všech režimech (Dnes, Týden, Měsíc), už od 1. dne po splatnosti.
  const spl = dnes?.skupiny?.poSplatnosti || [];
  const kartaSplatnosti = spl.length > 0 && (
    <KartaObal className="ring-2 ring-rose-200">
      <PruhSkupiny title="Zkušební po splatnosti" pocet={spl.length} ton="problem" />
      {spl.map((k, i) => {
        const z = k.zkusebni || {};
        return (
          <RadekKontaktu key={`spl-${k.id}`} prvni={i === 0} jmeno={k.jmeno} vybrany={siroka && vybrany === k.id} onOtevrit={() => otevri(k.id)}
            pilulky={<>
              <Pil ton={z.dni_po_splatnosti >= 2 ? "problem" : "pozor"} casti={[`${z.dni_po_splatnosti}. den po splatnosti`, z.castka ? `${Number(z.castka).toLocaleString("cs-CZ")} Kč` : null]} />
              <Pil ton="info" ikona={Clock} casti={[`zkušební ${denKratce(z.datum)}`, z.cas ? casBezNuly(z.cas) : null]} />
              {z.trener && <Pil ton="neutralni" ikona={UserRound} casti={[z.trener]} />}
            </>}
            poznamka={z.dni_po_splatnosti >= 2 ? "SMS o platbě odešla. Zavolej a domluv platbu, jinak zkušební trénink zruš."
              : z.sms_splatnost ? "SMS o platbě dnes odešla. Když nezaplatí, zítra zavolej." : "SMS o platbě odejde dnes během dne. Když nezaplatí, zítra zavolej."} />
        );
      })}
    </KartaObal>
  );

  const prepinac = (
    <Segment aria-label="Plán na" value={rezim} onChange={(r) => nastav({ p: r === "dnes" ? null : r })}
      items={[{ key: "dnes", label: "Dnes" }, { key: "tyden", label: "Týden" }, { key: "mesic", label: "Měsíc" }]} />
  );

  const seznam = rezim !== "dnes" ? <PlanVolani rezim={rezim} vybrany={siroka ? vybrany : null} onOtevrit={otevri} /> : !dnes
    ? <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-brand-light" />)}</div>
    : !skupiny.length
      // Když čeká jen volání po splatnosti (karta nahoře), „hotovo“ se neukazuje.
      ? spl.some((k) => (k.zkusebni?.dni_po_splatnosti || 0) >= 2) ? null : <KartaObal><EmptyState icon={PhoneCall} title="Na dnes je hotovo" hint="Nikdo další dnes nečeká na zavolání. Nové kontakty se tu objeví samy." /></KartaObal>
      : (
        <div className="space-y-4">
          {skupiny.map((g) => (
            <KartaObal key={g.klic}>
              <PruhSkupiny title={g.title} pocet={g.polozky.length} ton={g.ton} />
              {g.polozky.map((p, i) => (
                <RadekKontaktu key={`${g.klic}-${p.k.id}`} jmeno={p.k.jmeno} pilulky={p.pilulky} poznamka={p.pozn}
                  vybrany={siroka && vybrany === p.k.id} onOtevrit={() => otevri(p.k.id)} />
              ))}
            </KartaObal>
          ))}
        </div>
      );

  if (!siroka) return <div className="space-y-4">{jeSales && <SalesNastenka />}{dlazdice}{kartaSplatnosti}{prepinac}{seznam}</div>;
  return (
    <div className="space-y-4 sm:space-y-6">
      {jeSales && <SalesNastenka />}
      {dlazdice}
      {kartaSplatnosti && <div className="max-w-[720px]">{kartaSplatnosti}</div>}
      <div className="max-w-[420px]">{prepinac}</div>
      <div className="grid grid-cols-[minmax(0,420px)_minmax(0,1fr)] items-start gap-6">
        <div>{seznam}</div>
        <div>
          {vybrany ? <KartaKontaktu key={vybrany} id={vybrany} onZmena={poZmene} /> : (dnes && <KartaObal><EmptyState icon={PhoneCall} title="Vyber kontakt" hint="Klepni na šipku u jména vlevo." /></KartaObal>)}
        </div>
      </div>
    </div>
  );
}
